import React, { useEffect, useState, useCallback } from "react";
import { Clock, Pencil, Camera } from "lucide-react";
import { Card, Button, Badge, Spinner, PageHeader, Input, Modal } from "../../lib/ui";
import { useToast } from "../../lib/toast";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { BranchFilter } from "../../components/finance";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }); } catch { return "—"; }
}

export default function HRAttendance() {
  const toast = useToast();
  const { user } = useAuth();
  const isBranchMgr = user?.role === "BRANCH_MANAGER";
  const canCorrect = ["SUPER_ADMIN", "HR_MANAGER", "BRANCH_MANAGER"].includes(user?.role);
  const [branchId, setBranchId] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [photo, setPhoto] = useState(null);

  const viewPhoto = async (att, kind) => {
    setPhoto("loading");
    try {
      const res = await api.get(`/hr/attendance/${att.id}/photo/${kind}`, { responseType: "blob" });
      setPhoto({ url: URL.createObjectURL(res.data), name: att.employee_name, kind });
    } catch { toast.error("Foto tidak tersedia"); setPhoto(null); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hr/attendance", { params: { branch_id: branchId || undefined, date } });
      setRows(data.rows);
    } catch { toast.error("Gagal memuat"); } finally { setLoading(false); }
  }, [branchId, date, toast]);
  useEffect(() => { load(); }, [load]);

  const saveCorrection = async () => {
    try { await api.patch(`/hr/attendance/${edit.id}`, { check_in: edit.check_in, check_out: edit.check_out, note: edit.note }); toast.success("Dikoreksi"); setEdit(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  return (
    <div data-testid="hr-attendance-page">
      <PageHeader title="Absensi" subtitle="Kehadiran, keterlambatan & lembur" icon={Clock}>
        {!isBranchMgr && <BranchFilter value={branchId} onChange={setBranchId} testid="hr-att-branch-filter" />}
        <input type="date" className="cf-input !w-44 !py-2 !text-sm" value={date} onChange={(e) => setDate(e.target.value)} data-testid="hr-att-date" />
      </PageHeader>

      {loading ? <div className="flex h-64 items-center justify-center"><Spinner className="h-8 w-8" /></div> : (
        <Card className="overflow-x-auto" data-testid="hr-attendance-table">
          <table className="w-full text-sm">
            <thead className="border-b border-line text-left text-xs uppercase text-ink-mute">
              <tr><th className="py-2 pr-3">Karyawan</th><th className="py-2 pr-3">Cabang</th><th className="py-2 pr-3">Check In</th><th className="py-2 pr-3">Check Out</th><th className="py-2 pr-3 text-center">Status</th><th className="py-2 pr-3 text-right">Telat</th><th className="py-2 pr-3 text-right">Jam</th><th className="py-2 pr-3 text-right">Lembur</th><th className="py-2 pr-3">GPS</th><th className="py-2 pr-3 text-center">Foto</th>{canCorrect && <th />}</tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.length === 0 && <tr><td colSpan={11} className="py-8 text-center text-ink-mute">Tidak ada data absensi untuk tanggal ini.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} data-testid={`hr-att-row-${r.id}`}>
                  <td className="py-2.5 pr-3 font-medium text-ink">{r.employee_name}</td>
                  <td className="py-2.5 pr-3 text-ink-soft">{r.branch_name || "—"}</td>
                  <td className="py-2.5 pr-3 text-success-text">{fmt(r.check_in)}</td>
                  <td className="py-2.5 pr-3 text-orange-600">{fmt(r.check_out)}</td>
                  <td className="py-2.5 pr-3 text-center">
                    {!r.check_in ? <Badge tone="danger">Absen</Badge> : r.late_minutes > 0 ? <Badge tone="warn">Telat</Badge> : <Badge tone="success">Tepat Waktu</Badge>}
                  </td>
                  <td className="py-2.5 pr-3 text-right">{r.late_minutes > 0 ? <span className="text-amber-600">{r.late_minutes}m</span> : "—"}</td>
                  <td className="py-2.5 pr-3 text-right text-ink-soft">{r.worked_hours || 0}</td>
                  <td className="py-2.5 pr-3 text-right text-ink-soft">{r.overtime_hours || 0}</td>
                  <td className="py-2.5 pr-3 text-xs text-ink-mute" data-testid={`hr-att-gps-${r.id}`}>
                    {r.check_in_lat != null ? <span title={`${r.check_in_lat?.toFixed?.(5)}, ${r.check_in_lng?.toFixed?.(5)}`}>{r.check_in_distance_m != null ? `${Math.round(r.check_in_distance_m)}m` : "—"} · ±{r.check_in_accuracy ?? "?"}m</span> : "—"}
                  </td>
                  <td className="py-2.5 pr-3 text-center" data-testid={`hr-att-photo-${r.id}`}>
                    <div className="flex justify-center gap-1">
                      {r.check_in_photo && <button onClick={() => viewPhoto(r, "in")} className="rounded-lg p-1 text-emerald-600 hover:bg-emerald-50" title="Foto Check In" data-testid={`hr-att-photo-in-${r.id}`}><Camera size={15} /></button>}
                      {r.check_out_photo && <button onClick={() => viewPhoto(r, "out")} className="rounded-lg p-1 text-orange-600 hover:bg-orange-50" title="Foto Check Out" data-testid={`hr-att-photo-out-${r.id}`}><Camera size={15} /></button>}
                      {!r.check_in_photo && !r.check_out_photo && <span className="text-ink-mute">—</span>}
                    </div>
                  </td>
                  {canCorrect && <td className="py-2.5 pr-3 text-right"><button onClick={() => setEdit({ ...r })} className="rounded-lg p-1.5 text-ink-mute hover:bg-stone-100" data-testid={`hr-att-edit-${r.id}`}><Pencil size={15} /></button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Modal open={!!edit} onClose={() => setEdit(null)} title="Koreksi Absensi" size="sm"
        footer={<><Button variant="outline" onClick={() => setEdit(null)}>Batal</Button><Button onClick={saveCorrection} data-testid="hr-att-correction-save">Simpan</Button></>}>
        {edit && (
          <div className="space-y-3">
            <p className="text-sm font-medium text-ink">{edit.employee_name} · {edit.date}</p>
            <Input label="Check In (ISO)" value={edit.check_in || ""} onChange={(e) => setEdit({ ...edit, check_in: e.target.value })} />
            <Input label="Check Out (ISO)" value={edit.check_out || ""} onChange={(e) => setEdit({ ...edit, check_out: e.target.value })} />
            <Input label="Catatan" value={edit.note || ""} onChange={(e) => setEdit({ ...edit, note: e.target.value })} />
          </div>
        )}
      </Modal>

      <Modal open={!!photo} onClose={() => setPhoto(null)} title="Foto Absensi" size="sm">
        {photo === "loading" ? <div className="flex h-40 items-center justify-center"><Spinner className="h-7 w-7" /></div> : photo && (
          <div className="text-center" data-testid="hr-att-photo-modal">
            <p className="mb-2 text-sm font-medium text-ink">{photo.name} · {photo.kind === "in" ? "Check In" : "Check Out"}</p>
            <img src={photo.url} alt="absensi" className="mx-auto rounded-xl" />
          </div>
        )}
      </Modal>
    </div>
  );
}
